import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn, selectAllOnFocus } from "@/lib/utils";
import {
  format,
  isAfter,
  isBefore,
  isValid,
  parse,
  parseISO,
  startOfDay,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { IMaskInput } from "react-imask";
import { Button } from "./button";

interface DatePickerProps {
  /** ISO date (`yyyy-MM-dd`), or an empty string when there is no date. */
  value: string;
  /** Called with an ISO date, or an empty string when the input is cleared. */
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  className?: string;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  id?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean | "true" | "false";
  "data-testid"?: string;
}

const toDisplay = (iso: string) => {
  if (!iso) return "";
  const parsed = parseISO(iso);
  return isValid(parsed) ? format(parsed, "dd/MM/yyyy") : "";
};

/**
 * Controlled date input: a `dd/MM/yyyy` masked text field with a calendar
 * popover. Works without react-hook-form; `DateField` wraps it for forms.
 */
const DatePicker = ({
  value,
  onChange,
  onBlur,
  placeholder = "Selecione uma data",
  className,
  minDate,
  maxDate,
  disabled,
  id,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
  "data-testid": testId,
}: DatePickerProps) => {
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [inputValue, setInputValue] = useState(() => toDisplay(value));

  useEffect(() => {
    setInputValue(toDisplay(value));
  }, [value]);

  const selectedDate =
    value && isValid(parseISO(value)) ? parseISO(value) : undefined;

  const isOutOfRange = (date: Date) =>
    (minDate !== undefined &&
      isBefore(startOfDay(date), startOfDay(minDate))) ||
    (maxDate !== undefined && isAfter(startOfDay(date), startOfDay(maxDate)));

  const emit = (date: Date) => {
    const iso = format(date, "yyyy-MM-dd");
    if (iso !== value) onChange(iso);
  };

  const handleInputChange = (text: string) => {
    setInputValue(text);

    const digitsOnly = text.replace(/\D/g, "");

    if (digitsOnly.length === 0) {
      if (value !== "") onChange("");
      return;
    }

    if (digitsOnly.length === 8) {
      const parsed = parse(text, "dd/MM/yyyy", new Date());
      if (isValid(parsed) && !isOutOfRange(parsed)) emit(parsed);
    }
  };

  const disabledDays = [
    minDate ? { before: minDate } : undefined,
    maxDate ? { after: maxDate } : undefined,
  ].filter((matcher) => matcher !== undefined);

  return (
    <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
      <div className={cn("flex border rounded-md", className)}>
        <div className="relative grow">
          <PopoverTrigger asChild>
            <div className="w-full cursor-pointer">
              <IMaskInput
                mask="00/00/0000"
                value={inputValue}
                onAccept={(text: string) => handleInputChange(text)}
                onBlur={() => {
                  // Drop text that never became a valid date so the field
                  // always shows what the parent actually holds.
                  setInputValue(toDisplay(value));
                  onBlur?.();
                }}
                // A full mask rejects extra digits, so typing over a
                // filled date only works from a full selection.
                {...selectAllOnFocus}
                id={id}
                aria-describedby={ariaDescribedBy}
                aria-invalid={ariaInvalid}
                data-testid={testId}
                disabled={disabled}
                placeholder={placeholder}
                className={cn(
                  "bg-transparent outline-hidden text-sm flex w-full rounded-md px-3 py-2",
                  !value && "text-muted-foreground",
                )}
              />
            </div>
          </PopoverTrigger>
        </div>
        <div className="relative">
          <Button
            type="button"
            variant="ghost"
            className="rounded-l-none"
            disabled={disabled}
            aria-label="Abrir calendário"
            onClick={() => setCalendarOpen(true)}
          >
            <CalendarIcon className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <PopoverContent
        className="w-auto p-0"
        align="start"
        // Clicking the input opens the calendar; keep focus in the input
        // so typed digits land there instead of on a calendar day.
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <Calendar
          locale={ptBR}
          mode="single"
          selected={selectedDate}
          onSelect={(date) => {
            if (date && isValid(date)) {
              emit(date);
              setCalendarOpen(false);
            }
          }}
          defaultMonth={selectedDate ?? new Date()}
          disabled={disabledDays.length > 0 ? disabledDays : undefined}
          startMonth={new Date(2000, 0)}
          endMonth={new Date(2035, 11)}
        />
      </PopoverContent>
    </Popover>
  );
};

export default DatePicker;
